import { loadPlaces, mapPlaceAddress, findCountryOption } from './haro-clinic-places.js';

// Google search enhances the address section; every native field remains editable.
export function initializeWholesalePlaces(host, form, loader = loadPlaces) {
  const key = host?.dataset.apiKey?.trim();
  if (!key || !form || form.querySelector('[data-wholesale-result="success"]')) return;
  const status = host.querySelector('[data-places-status]');
  const trigger = host.querySelector('[data-places-start]');
  const mount = host.querySelector('[data-places-widget]');
  const credits = host.querySelector('[data-places-attributions]');
  const names = ['business', 'business_address', 'business_suite', 'business_city', 'business_region', 'business_postal_code', 'business_country', 'phone', 'website'];
  const field = name => form.elements.namedItem(`contact[${name}]`);
  let selection = 0;
  let started = false;
  const filled = new Map();
  host.hidden = false;
  form.addEventListener('input', event => {
    if (names.some(name => event.target === field(name))) {
      selection++;
      status.textContent = '';
    }
  });
  form.addEventListener('change', event => {
    if (event.isTrusted && names.some(name => event.target === field(name))) selection++;
  });
  const setField = (name, value) => {
    const input = field(name);
    if (!input) return;
    // Keep the applicant's manually entered phone/website; replace previous lookup data.
    if (['phone', 'website'].includes(name) && input.value && input.value !== filled.get(name)) return;
    input.value = String(value || '').slice(0, input.maxLength > 0 ? input.maxLength : 2000);
    filled.set(name, input.value);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };
  const initialize = async () => {
    if (started) return;
    started = true;
    if (trigger) trigger.disabled = true;
    status.textContent = (window.haroMessages?.loading_business_search_you_can_enter_your_det_6c7c5 || "Loading business search… You can enter your details below while it loads.");
    try {
      const { PlaceAutocompleteElement } = await loader(key);
      const widget = new PlaceAutocompleteElement();
      widget.placeholder = (window.haroMessages?.business_name_or_street_address_07eb2 || "Business name or street address");
      widget.setAttribute('aria-label', (window.haroMessages?.search_for_your_business_or_address_bf23f || "Search for your business or address"));
      widget.setAttribute('aria-labelledby', 'WholesaleLookupLabel');
      widget.setAttribute('aria-describedby', 'WholesaleLookupHelp');
      widget.addEventListener('gmp-error', () => {
        selection++;
        status.textContent = (window.haroMessages?.business_search_is_unavailable_please_enter_yo_dcadd || "Business search is unavailable. Please enter your details below.");
      });
      widget.addEventListener('gmp-select', async ({ placePrediction }) => {
        const current = ++selection;
        status.textContent = (window.haroMessages?.getting_business_details_79df5 || "Getting business details…");
        try {
          const place = placePrediction.toPlace();
          await place.fetchFields({ fields: ['displayName', 'formattedAddress', 'addressComponents', 'internationalPhoneNumber', 'websiteURI'] });
          if (current !== selection) return;
          const address = mapPlaceAddress(place.addressComponents);
          let website = '';
          try {
            const parsed = new URL(place.websiteURI);
            if (['https:', 'http:'].includes(parsed.protocol)) website = parsed.href;
          } catch { /* Missing/invalid website stays manual. */ }
          setField('business', place.displayName || placePrediction.mainText?.toString());
          setField('business_address', address.address);
          setField('business_suite', address.suite);
          setField('business_city', address.city);
          setField('business_region', address.region);
          setField('business_postal_code', address.postal);
          setField('business_country', findCountryOption(field('business_country').options, address.countryCode, address.countryName));
          setField('phone', place.internationalPhoneNumber);
          setField('website', website);
          credits.replaceChildren();
          for (const attribution of place.attributions || []) {
            const item = document.createElement(attribution.providerURI?.startsWith('https://') ? 'a' : 'span');
            item.textContent = attribution.provider || '';
            if (item.tagName === 'A') {
              item.href = attribution.providerURI;
              item.target = '_blank';
              item.rel = 'noopener noreferrer';
            }
            credits.append(item, document.createTextNode(' '));
          }
          status.textContent = (window.haroMessages?.available_business_details_added_please_review_f8349 || "Available business details added. Please review them and complete anything missing.");
        } catch {
          if (current === selection) status.textContent = (window.haroMessages?.we_could_not_retrieve_that_business_please_ent_c2c1c || "We could not retrieve that business. Please enter your details below.");
        }
      });
      mount.append(widget);
      if (trigger) trigger.hidden = true;
      status.textContent = '';
    } catch {
      started = false;
      if (trigger) {
        trigger.disabled = false;
        trigger.hidden = false;
        trigger.textContent = (window.haroMessages?.try_google_business_search_again_039fc || "Try Google business search again");
      }
      status.textContent = (window.haroMessages?.business_search_is_unavailable_please_enter_yo_dcadd || "Business search is unavailable. Please enter your details below.");
    }
  };
  trigger?.addEventListener('click', initialize);
  const dialog = form.closest('dialog');
  if (dialog && typeof dialog.showModal === 'function') {
    dialog.addEventListener('haro:wholesale-open', initialize);
    if (dialog.open) initialize();
  } else initialize();
}

if (typeof document !== 'undefined') {
  initializeWholesalePlaces(document.querySelector('[data-wholesale-places]'), document.getElementById('HaroWholesaleForm'));
}
